import React, { useEffect, useState } from 'react';
import { Modal, Button, Radio } from 'antd';
import { PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';

interface Props {
  visible: boolean;
  onCancel: () => void;
  templateId: string;
  templateTitle: string;
  orgId: string;
}

const AuditTemplateQRCodeModal: React.FC<Props> = ({
  visible,
  onCancel,
  templateId,
  templateTitle,
  orgId,
}) => {
  const [qrCode, setQrCode] = useState<string>('');
  const [size, setSize] = useState<number>(300);

  useEffect(() => {
    if (visible && templateId && orgId) {
      generateQRCode();
    }
  }, [visible, templateId, orgId]);

  const generateQRCode = async () => {
    const qrData = JSON.stringify({ orgId, templateId });
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
        width: 400,
        margin: 2,
      });
      setQrCode(qrCodeDataUrl);
    } catch (error) {
      console.error('Failed to generate QR code', error);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Kode - ${templateTitle}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
            }
            .qr-container {
              text-align: center;
            }
            img {
              width: ${size}px;
              height: auto;
            }
            .title {
              font-size: 18px;
              font-weight: bold;
              margin-top: 10px;
              font-family: sans-serif;
            }
            @media print {
              @page {
                margin: 0;
                size: auto;
              }
              body {
                height: auto;
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${qrCode}" alt="QR Code" />
            <div class="title">${templateTitle}</div>
          </div>
          <script>
            window.onload = function() { 
              window.print(); 
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownload = () => {
    if (!qrCode) return;

    // Create a canvas to combine QR code and title
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Set canvas dimensions (QR code + padding + text area)
      const padding = 40;
      const textHeight = 60;
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2 + textHeight;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw QR code
      ctx.drawImage(img, padding, padding);

      // Draw title text
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(templateTitle, canvas.width / 2, img.height + padding + 40);

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `QR-${templateTitle.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    };
    img.src = qrCode;
  };

  return (
    <Modal
      title="QR Kode"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Luk
        </Button>,
        <Button key="download" icon={<DownloadOutlined />} onClick={handleDownload}>
          Download
        </Button>,
        <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print
        </Button>,
      ]}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        {qrCode && (
          <>
            <img
              src={qrCode}
              alt={`QR Code for ${templateTitle}`}
              style={{
                width: '100%',
                maxWidth: '300px',
                height: 'auto',
                marginBottom: '16px',
              }}
            />
            <div style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'center' }}>
              {templateTitle}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AuditTemplateQRCodeModal;
